import * as React from 'react';
import { CertificationIconComponent, CertificationIconProps } from './CertificationIcon';
import { CertifierComponent, CertifierProps } from './Certifier';
import { ChromeTooltipWidget } from './ChromeTooltip/ChromeTooltipWidget';
import { ExtractRefreshComponent } from './ExtractRefresh';
import { generateId, Id } from '../common/Id';
import {
  largeSpace,
  primaryFontColor,
  secondaryFontColor,
  tinyFontSize,
  tooltipSize,
  tooltipStyle
} from '../common/Style';
import { NoteComponent } from './Note';

const certifierStyle: React.CSSProperties = {
  display: 'block',
  fontWeight: 'bold',
  color: primaryFontColor,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const noteStyle: React.CSSProperties = {
  marginTop: 14,
  marginBottom: largeSpace,
  fontSize: tinyFontSize,
  color: secondaryFontColor
};

/**
 * Test id attribute value (prefix) for content of certification tooltip component.
 */
export const certificationTooltipTestId: Id = 'certification-tooltip';

/**
 * Props for certification tooltip component.
 */
export interface CertificationTooltipProps {
  certifier: CertifierProps;
  /**
   * If a string will use legacy markup rendering and styles, otherwise renders note as is.
   */
  note: string | React.ReactNode;
  icon?: React.ReactElement<CertificationIconProps>;
  date?: Date;
  id?: Id;
  className?: string;
  style?: React.CSSProperties;
  boundaryElement?: HTMLElement;
  // TODO remove after unifying stacking strategy in viz client, see feature 579115
  zIndex?: number;
  // This was added as a non-breaking change to allow certification for non-DS types
  showExtractRefresh?: boolean;
  // To disable the link on certifier tooltip or not (default to false)
  linksDisabled?: boolean;
}

/**
 * Certification tooltip component.
 * @param props - The props for this certification tooltip.
 */
export function CertificationTooltipComponent(props: CertificationTooltipProps): React.ReactElement<CertificationTooltipProps> {
  // NOTE this function could make an AJAX request
  const getContent = () => Promise.resolve(<Content {...props} />);
  return (
    <ChromeTooltipWidget getContent={getContent} width={tooltipSize} boundaryElement={props.boundaryElement} zIndex={props.zIndex}>
      {props.icon || <CertificationIconComponent id={props.id} className={props.className} style={props.style} />}
    </ChromeTooltipWidget>
  );
}

function Content(props: CertificationTooltipProps): React.ReactElement<CertificationTooltipProps> {
  const showExtractRefresh = props.showExtractRefresh === undefined ? true : props.showExtractRefresh;

  return (
    <div data-tb-test-id={generateId(certificationTooltipTestId, props.id)} style={tooltipStyle}>
      {
        showExtractRefresh && <ExtractRefreshComponent date={props.date} customStyle={certifierStyle} />
      }
      <CertifierComponent style={certifierStyle} {...props.certifier} newTab={true} linksDisabled={props.linksDisabled || false} />
      {
        typeof props.note === 'string' ? <NoteComponent style={noteStyle} note={props.note} /> : props.note
      }
    </div>
  );
}
